import { ref } from 'vue'
import type { CropperData, VueCropperMethods } from 'vue-cropperjs'

export const useImageProcessing = () => {
  const editFile = ref<string>('')
  const mimeType = ref<string>('')
  const imageWidth = ref<number>(0)
  const imageHeight = ref<number>(0)
  const imageFileSize = ref<number>(0)
  const cropWidth = ref<number>(0)
  const cropHeight = ref<number>(0)

  const detectMimeTypeFromImageSource = (imageSource: string): string => {
    if (!imageSource) return 'image/jpeg'

    if (imageSource.startsWith('data:')) {
      const mimeMatch = imageSource.match(/^data:([^;,]+)/i)
      return mimeMatch?.[1] || 'image/jpeg'
    }

    const normalizedSource = imageSource.split('?')[0].toLowerCase()

    if (normalizedSource.endsWith('.png')) return 'image/png'
    if (normalizedSource.endsWith('.webp')) return 'image/webp'
    if (normalizedSource.endsWith('.gif')) return 'image/gif'

    return 'image/jpeg'
  }

  const loadImageWithCORS = (
    imageUrl: string,
    onSuccess: (
      dataUrl: string,
      width: number,
      height: number,
      estimatedSize: number,
      detectedMimeType: string
    ) => void,
    onError: (shouldFallback: boolean) => void
  ): void => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = (): void => {
      try {
        const detectedMimeType = detectMimeTypeFromImageSource(imageUrl)
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          throw new Error('Failed to get canvas context')
        }

        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        const dataUrl =
          detectedMimeType === 'image/jpeg' || detectedMimeType === 'image/webp'
            ? canvas.toDataURL(detectedMimeType, 1)
            : canvas.toDataURL(detectedMimeType)
        const base64Length = dataUrl.split(',')[1]?.length || 0
        const estimatedSize = base64Length * 0.75

        onSuccess(dataUrl, img.width, img.height, estimatedSize, detectedMimeType)
      } catch (error) {
        onError(false)
      }
    }

    img.onerror = (): void => {
      onError(true)
    }

    img.src = imageUrl
  }

  const loadImageFallback = (
    imageUrl: string,
    onSuccess: (
      url: string,
      width: number,
      height: number,
      mimeType: string,
      estimatedSize: number
    ) => void,
    onError: () => void
  ): void => {
    const fallbackImg = new Image()

    fallbackImg.onload = (): void => {
      try {
        const detectedMimeType = detectMimeTypeFromImageSource(imageUrl)
        const estimatedSize = 1024 * 1024 // 1MB default
        onSuccess(imageUrl, fallbackImg.width, fallbackImg.height, detectedMimeType, estimatedSize)
      } catch (error) {
        onError()
      }
    }

    fallbackImg.onerror = onError
    fallbackImg.src = imageUrl
  }

  const initializeCropper = (
    cropperRef: VueCropperMethods | null,
    captureError?: (error: Error, context: string) => void,
    aspectRatio: number = 1
  ): void => {
    if (!cropperRef) return

    try {
      // Removed containerData as it's not used anymore
      const imageData = cropperRef.getImageData()

      const imgW = imageData.naturalWidth
      const imgH = imageData.naturalHeight

      let cropW: number
      let cropH: number

      // Determine if this is portrait orientation (ratio < 1) or landscape (ratio >= 1)
      const isPortrait = aspectRatio < 1

      // Always use maximum area that fits the aspect ratio
      if (isPortrait) {
        // For portrait orientation, prioritize height
        cropH = imgH
        cropW = cropH * aspectRatio

        // If width exceeds image width, recalculate
        if (cropW > imgW) {
          cropW = imgW
          cropH = cropW / aspectRatio
        }
      } else {
        // For landscape or square, use standard approach
        if (imgW / imgH >= aspectRatio) {
          cropH = imgH
          cropW = cropH * aspectRatio
        } else {
          cropW = imgW
          cropH = cropW / aspectRatio
        }
      }

      const x = (imgW - cropW) / 2
      const y = (imgH - cropH) / 2

      cropperRef.setData({
        x,
        y,
        width: cropW,
        height: cropH,
        rotate: 0,
        scaleX: 1,
        scaleY: 1,
      })

      cropWidth.value = cropW
      cropHeight.value = cropH
    } catch (error) {
      if (captureError) {
        captureError(error as Error, 'initializeCropper')
      } else {
        console.error('Error initializing cropper:', error)
      }
    }
  }

  const ensureImageSize = (
    data: CropperData,
    cropperRef: VueCropperMethods | null,
    maxHeight: number,
    captureError?: (error: Error, context: string) => void
  ): void => {
    try {
      if (data.y < 0) {
        data.y = 0
      }
      if (data.height + data.y > maxHeight) {
        const offset = data.height + data.y - maxHeight
        data.y = data.y - offset
        if (data.y < 0) {
          data.y = 0
          data.height = data.height - offset
        }
      }
      if (cropperRef) {
        cropperRef.setData(data)
      }
    } catch (error) {
      if (captureError) {
        captureError(error as Error, 'ensureImageSize')
      } else {
        console.error('Error ensuring image size:', error)
      }
    }
  }

  const createBlobFromCanvas = async (
    canvas: HTMLCanvasElement,
    mimeType: string,
    quality?: number
  ): Promise<Blob> => {
    try {
      return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) {
              resolve(result)
            } else {
              reject(new Error('Failed to create blob from canvas'))
            }
          },
          mimeType,
          quality
        )
      })
    } catch (corsError) {
      try {
        const dataUrl =
          quality === undefined ? canvas.toDataURL(mimeType) : canvas.toDataURL(mimeType, quality)
        if (!dataUrl || dataUrl === 'data:,') {
          throw new Error('Failed to create data URL from canvas')
        }
        const response = await fetch(dataUrl)
        return await response.blob()
      } catch (fallbackError) {
        throw new Error('Failed to create image data from canvas')
      }
    }
  }

  const createFileFromCanvas = async (
    canvas: HTMLCanvasElement,
    mimeTypeValue: string,
    options: {
      maxBytes?: number
      preferredMaxBytes?: number
    } = {}
  ): Promise<File> => {
    let blob: Blob
    const normalizedMimeType = mimeTypeValue || 'image/jpeg'
    const isLossyFormat = normalizedMimeType === 'image/jpeg' || normalizedMimeType === 'image/webp'
    const requestedMaxBytes = options.maxBytes
    const preferredMaxBytes = options.preferredMaxBytes
    const targetMaxBytes =
      requestedMaxBytes && preferredMaxBytes
        ? Math.min(requestedMaxBytes, preferredMaxBytes)
        : requestedMaxBytes || preferredMaxBytes

    if (isLossyFormat) {
      const qualitySteps = [0.92, 0.88, 0.84, 0.8, 0.76, 0.72, 0.68, 0.64, 0.6]
      let bestBlob: Blob | null = null

      for (const quality of qualitySteps) {
        const candidateBlob = await createBlobFromCanvas(canvas, normalizedMimeType, quality)

        if (!bestBlob || candidateBlob.size < bestBlob.size) {
          bestBlob = candidateBlob
        }

        if (!targetMaxBytes || candidateBlob.size <= targetMaxBytes) {
          blob = candidateBlob
          break
        }
      }

      if (!blob) {
        blob = bestBlob || (await createBlobFromCanvas(canvas, normalizedMimeType, 0.6))
      }
    } else {
      blob = await createBlobFromCanvas(canvas, normalizedMimeType)
    }

    if (!blob || blob.size === 0) {
      throw new Error('Invalid blob created - blob is null or empty')
    }

    const extensionMap: Record<string, string> = {
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif',
      'image/jpeg': 'jpg',
    }
    const extension = extensionMap[normalizedMimeType] || 'jpg'
    const fileName = `edited-image-${Date.now()}.${extension}`
    const file = new File([blob], fileName, {
      type: blob.type || normalizedMimeType,
      lastModified: Date.now(),
    })

    if (!file || file.size === 0) {
      throw new Error('Invalid file created - file is null or empty')
    }

    // Verify the file can be read
    try {
      await file.arrayBuffer()
    } catch (readError) {
      throw new Error('File cannot be read properly')
    }

    return file
  }

  const resetImageData = (): void => {
    editFile.value = ''
    mimeType.value = ''
    imageWidth.value = 0
    imageHeight.value = 0
    imageFileSize.value = 0
    cropWidth.value = 0
    cropHeight.value = 0
  }

  return {
    // Refs
    editFile,
    mimeType,
    imageWidth,
    imageHeight,
    imageFileSize,
    cropWidth,
    cropHeight,

    // Methods
    loadImageWithCORS,
    loadImageFallback,
    initializeCropper,
    ensureImageSize,
    createFileFromCanvas,
    resetImageData,
  }
}
